import { useRef, useState } from 'react';
import {
  X,
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useBulkCreateAssetVendors } from '../../../services/httpServices/queries';
import { parseCsv, toCsv, downloadTextFile } from '../../../utils/csv';
import { toast } from 'sonner';
import type { CreateAssetVendorDto } from '../../../types/asset';

interface AssetVendorCsvImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported?: () => void;
}

/** Canonical column name for each header spelling the importer understands. */
const CSV_HEADERS: Record<string, string> = {
  name: 'name',
  vendorname: 'name',
  vendor: 'name',

  vendorcode: 'vendorCode',
  code: 'vendorCode',

  contactperson: 'contactPerson',
  contact: 'contactPerson',
  contactname: 'contactPerson',

  phonenumber: 'phoneNumber',
  phone: 'phoneNumber',
  mobile: 'phoneNumber',
  contactnumber: 'phoneNumber',

  email: 'email',
  emailaddress: 'email',

  location: 'location',
  city: 'city',
  address: 'address',
  fulladdress: 'address',

  taxid: 'taxId',
  taxnumber: 'taxId',
  tin: 'taxId',
  vatnumber: 'taxId',

  paymenttermsdays: 'paymentTermsDays',
  paymentterms: 'paymentTermsDays',
  terms: 'paymentTermsDays',
  creditdays: 'paymentTermsDays',

  isactive: 'isActive',
  active: 'isActive',
  status: 'isActive',

  notes: 'notes',
  note: 'notes',
  remarks: 'notes',
  comment: 'notes',
  comments: 'notes',
};

interface ParsedVendorRow {
  rowNumber: number;
  name: string;
  vendorCode: string;
  contactPerson: string;
  phoneNumber: string;
  email: string;
  location: string;
  paymentTermsDays: number;
  isActive: boolean;
  data: CreateAssetVendorDto;
  error?: string;
}

interface BulkResult {
  ok: number;
  failed: { rowNumber: number; message: string }[];
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_-]+/g, '');
}

function parseBoolean(raw: string): boolean | undefined {
  const v = raw.trim().toLowerCase();
  if (!v) return undefined;
  if (['true', '1', 'yes', 'y', 'active', 'enabled'].includes(v)) return true;
  if (['false', '0', 'no', 'n', 'inactive', 'disabled'].includes(v)) return false;
  return undefined;
}

function parseVendorCsv(text: string): { rows: ParsedVendorRow[]; fileErrors: string[] } {
  const matrix = parseCsv(text);
  if (matrix.length === 0) return { rows: [], fileErrors: ['The file is empty.'] };

  const header = matrix[0].map((h) => CSV_HEADERS[normalizeHeader(h)] || '');
  if (!header.includes('name')) {
    return { rows: [], fileErrors: ['Missing required column: name.'] };
  }

  const seenCodes = new Set<string>();
  const rows: ParsedVendorRow[] = [];

  for (let i = 1; i < matrix.length; i += 1) {
    const raw: Record<string, string> = {};
    header.forEach((key, idx) => {
      if (key) raw[key] = (matrix[i][idx] ?? '').trim();
    });

    const problems: string[] = [];

    const name = raw.name || '';
    if (!name) problems.push('Name is required');

    const vendorCode = raw.vendorCode || '';
    if (vendorCode) {
      const key = vendorCode.toLowerCase();
      if (seenCodes.has(key)) problems.push(`Duplicate vendor code "${vendorCode}" in this file`);
      else seenCodes.add(key);
    }

    let paymentTermsDays = 0;
    const termsRaw = raw.paymentTermsDays || '';
    if (termsRaw) {
      const parsedTerms = Number(termsRaw);
      if (!Number.isInteger(parsedTerms) || parsedTerms < 0) {
        problems.push(`Payment terms must be a non-negative whole number (got "${termsRaw}")`);
      } else {
        paymentTermsDays = parsedTerms;
      }
    }

    let isActive = true;
    const activeRaw = raw.isActive || '';
    if (activeRaw) {
      const parsedActive = parseBoolean(activeRaw);
      if (parsedActive === undefined) problems.push(`Status must be active or inactive (got "${activeRaw}")`);
      else isActive = parsedActive;
    }

    const email = raw.email || '';
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      problems.push(`Invalid email "${email}"`);
    }

    rows.push({
      rowNumber: i + 1,
      name,
      vendorCode,
      contactPerson: raw.contactPerson || '',
      phoneNumber: raw.phoneNumber || '',
      email,
      location: raw.location || raw.city || '',
      paymentTermsDays,
      isActive,
      data: {
        name,
        vendorCode: vendorCode || undefined,
        contactPerson: raw.contactPerson || undefined,
        phoneNumber: raw.phoneNumber || undefined,
        email: email || undefined,
        location: raw.location || undefined,
        city: raw.city || undefined,
        address: raw.address || undefined,
        taxId: raw.taxId || undefined,
        paymentTermsDays,
        isActive,
        notes: raw.notes || undefined,
      },
      error: problems.length ? problems.join('; ') : undefined,
    });
  }

  return { rows, fileErrors: [] };
}

export function AssetVendorCsvImportModal({ open, onClose, onImported }: AssetVendorCsvImportModalProps) {
  const bulkCreateVendors = useBulkCreateAssetVendors();
  const fileRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ParsedVendorRow[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [result, setResult] = useState<BulkResult | null>(null);

  if (!open) return null;

  const validRows = rows.filter((r) => !r.error);

  const reset = () => {
    setFileName('');
    setRows([]);
    setFileErrors([]);
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const downloadSample = () => {
    const sample = toCsv([
      [
        'name',
        'vendorCode',
        'contactPerson',
        'phoneNumber',
        'email',
        'location',
        'city',
        'address',
        'taxId',
        'paymentTermsDays',
        'isActive',
        'notes',
      ],
      [
        'MedSupply Ltd',
        'AV-101',
        'Rahim Uddin',
        '01700000001',
        'sales@medsupply.com',
        'Dhanmondi',
        'Dhaka',
        'House 12, Road 5',
        'TIN-123456',
        '30',
        'active',
        'Primary equipment supplier',
      ],
      [
        'Pharma Distributors',
        'AV-102',
        'Karim Ali',
        '01700000002',
        'info@pharmadist.com',
        'Gulshan',
        'Dhaka',
        'Plot 7, Avenue 3',
        'TIN-654321',
        '15',
        'active',
        'Medicines and consumables',
      ],
    ]);
    downloadTextFile('vendor-import-sample.csv', sample);
  };

  const handleFile = async (file?: File) => {
    if (!file) return;
    setResult(null);
    setFileName(file.name);
    setFileErrors([]);
    setRows([]);
    try {
      const text = await file.text();
      const parsed = parseVendorCsv(text);
      setRows(parsed.rows);
      setFileErrors(parsed.fileErrors);
      if (parsed.fileErrors.length > 0) {
        toast.error(parsed.fileErrors[0]);
      } else if (parsed.rows.length === 0) {
        toast.error('No vendor rows found in the CSV');
      } else {
        const invalid = parsed.rows.filter((r) => r.error).length;
        if (invalid > 0) {
          toast.warning(
            `${parsed.rows.length - invalid} of ${parsed.rows.length} row(s) ready · ${invalid} need attention`,
          );
        } else {
          toast.success(`${parsed.rows.length} vendor row(s) ready to import`);
        }
      }
    } catch {
      toast.error('Could not read the CSV file');
    }
  };

  const handleImport = async () => {
    if (validRows.length === 0) return;
    setResult(null);
    try {
      const res = await bulkCreateVendors.mutateAsync(
        validRows.map((r) => ({ rowNumber: r.rowNumber, data: r.data })),
      );
      setResult(res);
      setRows([]);
      setFileName('');
      if (fileRef.current) fileRef.current.value = '';
      if (res.ok > 0) {
        toast.success(`Imported ${res.ok} vendor${res.ok === 1 ? '' : 's'}`);
        onImported?.();
      }
      if (res.failed.length > 0) {
        toast.error(`${res.failed.length} row(s) failed to import`);
      }
    } catch {
      toast.error('Import failed');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-border">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-xl bg-primary/10 shrink-0">
              <FileSpreadsheet size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-text">Import vendors from CSV</h2>
              <p className="text-xs text-text-muted">Upload a spreadsheet to create vendors in bulk</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 hover:bg-background rounded-xl transition-colors shrink-0"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="rounded-lg border border-border bg-background/60 p-3 text-xs text-text-muted space-y-1">
            <p>
              Required column: <span className="font-mono text-text">name</span>.
            </p>
            <p>
              Optional columns: <span className="font-mono text-text">vendorCode</span>,{' '}
              <span className="font-mono text-text">contactPerson</span>,{' '}
              <span className="font-mono text-text">phoneNumber</span>,{' '}
              <span className="font-mono text-text">email</span>,{' '}
              <span className="font-mono text-text">location</span>,{' '}
              <span className="font-mono text-text">city</span>,{' '}
              <span className="font-mono text-text">address</span>,{' '}
              <span className="font-mono text-text">taxId</span>,{' '}
              <span className="font-mono text-text">paymentTermsDays</span>,{' '}
              <span className="font-mono text-text">isActive</span> and{' '}
              <span className="font-mono text-text">notes</span>.
            </p>
            <p>Leave vendorCode blank to auto-generate one.</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={downloadSample}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-text border border-border rounded-lg hover:bg-background transition-colors"
            >
              <Download size={15} />
              Download CSV Sample
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-sm rounded-lg hover:opacity-90 transition-opacity"
            >
              <Upload size={15} />
              Choose CSV File
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                handleFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            {fileName && (
              <span className="text-xs text-text-muted truncate max-w-[180px]" title={fileName}>
                {fileName}
              </span>
            )}
          </div>

          {fileErrors.length > 0 && (
            <div className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-xs text-danger space-y-1">
              {fileErrors.map((msg) => (
                <p key={msg} className="flex items-center gap-1.5">
                  <AlertCircle size={13} />
                  {msg}
                </p>
              ))}
            </div>
          )}

          {rows.length > 0 && (
            <div>
              <p className="text-xs text-text-muted mb-2">
                <span className="font-medium text-text">{validRows.length}</span> of {rows.length} row(s)
                ready to import
              </p>
              <div className="max-h-64 overflow-auto rounded-lg border border-border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-background/95">
                    <tr className="border-b border-border">
                      <th className="px-2 py-1.5 text-left text-text-muted font-medium">#</th>
                      <th className="px-2 py-1.5 text-left text-text-muted font-medium">Name</th>
                      <th className="px-2 py-1.5 text-left text-text-muted font-medium">Code</th>
                      <th className="px-2 py-1.5 text-left text-text-muted font-medium">Contact</th>
                      <th className="px-2 py-1.5 text-left text-text-muted font-medium">Terms</th>
                      <th className="px-2 py-1.5 text-left text-text-muted font-medium">Status</th>
                      <th className="px-2 py-1.5 text-left text-text-muted font-medium">Validation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rows.map((row) => (
                      <tr key={row.rowNumber} className={row.error ? 'bg-danger/5' : ''}>
                        <td className="px-2 py-1.5 text-text-muted">{row.rowNumber}</td>
                        <td className="px-2 py-1.5 text-text">{row.name || '—'}</td>
                        <td className="px-2 py-1.5 font-mono text-text-muted">{row.vendorCode || 'auto'}</td>
                        <td className="px-2 py-1.5 text-text-muted">{row.contactPerson || '—'}</td>
                        <td className="px-2 py-1.5 text-text-muted">{row.paymentTermsDays} days</td>
                        <td className="px-2 py-1.5">
                          <span className={row.isActive ? 'text-success' : 'text-text-muted'}>
                            {row.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-2 py-1.5">
                          {row.error ? (
                            <span className="text-danger" title={row.error}>
                              {row.error}
                            </span>
                          ) : (
                            <span className="text-success inline-flex items-center gap-1">
                              <CheckCircle2 size={12} /> Ready
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className="rounded-lg border border-border bg-background/60 p-3 text-xs space-y-1">
              <p className="text-text flex items-center gap-1.5">
                <CheckCircle2 size={13} className="text-success" />
                Imported <span className="font-semibold text-success">{result.ok}</span> vendor
                {result.ok === 1 ? '' : 's'}.
              </p>
              {result.failed.length > 0 && (
                <div className="text-danger space-y-0.5">
                  {result.failed.map((f) => (
                    <p key={f.rowNumber} className="flex items-center gap-1.5">
                      <AlertCircle size={12} />
                      Row {f.rowNumber}: {f.message}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
          <Button type="button" variant="outline" onClick={handleClose}>
            {result ? 'Done' : 'Cancel'}
          </Button>
          <Button
            type="button"
            onClick={handleImport}
            disabled={bulkCreateVendors.isPending || validRows.length === 0}
            isLoading={bulkCreateVendors.isPending}
          >
            {!bulkCreateVendors.isPending && <Upload size={15} className="mr-2" />}
            Import {validRows.length} Vendor{validRows.length === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </div>
  );
}
